'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { FATHOM_EVENTS } from '@/lib/fathom/fathom-events';
import { trackFathomEvent } from '@/lib/fathom/trackFathomEvent';
import type { AboutNavItem } from '@/sections/about/copy';

import { ACTIVE_TAB_GRADIENT } from '../theme';
import { pickActiveId } from './activeSection';

interface AboutNavProps {
  items: AboutNavItem[];
}

// Sticky anchor nav for the about page, carried straight on from the dark hero: a raised
// near-black band (gray-10) with hairline-divided uppercase tabs. The active tab takes the
// teal→violet gradient fill with dark text; the rest sit near-white. The active tab is derived
// from scroll position by ./activeSection.ts — see there for why an IntersectionObserver was
// the wrong tool. Anchor links are <a> (never next/link — it mangles hash-only hrefs). The
// treatment is constant whether pinned or at rest, so there is no motion here and no
// reduced-motion handling is required.
export function AboutNav({ items }: AboutNavProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const elements = items.map(item => document.getElementById(item.id)).filter((el): el is HTMLElement => el != null);
    if (elements.length === 0) return;

    let frame = 0;
    // Recomputed from ALL sections on every scroll rather than from an IntersectionObserver's
    // changed-entries subset, which could silently never fire for these very tall sections and
    // leave the highlight stuck on the previously clicked tab. rAF-coalesced, and the read is
    // a handful of getBoundingClientRect calls.
    const sync = () => {
      frame = 0;
      const barBottom = navRef.current?.getBoundingClientRect().bottom ?? 0;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      setActiveId(
        pickActiveId(
          elements.map(el => ({ id: el.id, top: el.getBoundingClientRect().top })),
          barBottom,
          atBottom,
        ),
      );
    };
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(sync);
    };

    sync();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    // Covers browser back/forward between hashes, which changes scroll without a click.
    window.addEventListener('hashchange', schedule);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('hashchange', schedule);
    };
  }, [items]);

  return (
    <nav
      ref={navRef}
      aria-label="Sections"
      // Sits directly under the sticky site header — --nav-height is the kraidle MegaNav's
      // own height token (responsive: 104px desktop / 99px mobile), so the two bars never
      // overlap. z-30 keeps it below the header (z-40).
      // Surfaces are the design's own variables, which have no repo equivalent:
      // bg = kraidle neutral-900 #171717, hairlines = `color/border/default` #262626.
      className="sticky top-[var(--nav-height,104px)] z-30 border-y border-[#262626] bg-[#171717]"
    >
      {/* overflow-x-auto: the three labels exceed 375px — let them scroll inside the bar
          rather than push the page body into a horizontal scroll. Alignment must be
          `start` while the row overflows: `justify-center` on a scroll container pushes
          the first tab past scrollLeft:0, making it permanently unreachable. The row fits
          from md up, where centring is safe. */}
      <div className="flex justify-start overflow-x-auto md:justify-center">
        {items.map(item => {
          const isActive = item.id === activeId;
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              // Deliberately does NOT set active state. There is exactly ONE source of truth for
              // the highlight — scroll position (see the effect above). A click just scrolls; the
              // resulting scroll events move the highlight. Storing state here as well gave two
              // paths that could disagree, which is what "the highlighting gets stuck" was.
              onClick={() => trackFathomEvent(FATHOM_EVENTS.about.nav.link.click)}
              aria-current={isActive ? 'true' : undefined}
              style={isActive ? { background: ACTIVE_TAB_GRADIENT } : undefined}
              className={clsx(
                // min-h must equal ABOUT_NAV_HEIGHT — written as a literal because Tailwind
                // only extracts statically-visible class names.
                'flex min-h-[74px] shrink-0 items-center justify-center border-x border-[#262626] px-6 py-3 text-caption-sm font-semibold tracking-[0.1333em] whitespace-nowrap uppercase transition-colors',
                // Active text = `color/text/inverse` #171717; idle = `color/text/primary` #fafafa.
                isActive ? 'text-[#171717] underline underline-offset-4' : 'text-[#fafafa] hover:text-white',
              )}
            >
              {item.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
